import React, { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import Spacing from "../Spacing";
import { Search, ChevronRight, Loader } from "lucide-react";
import Select from "react-select";
import { useDocumentTitle } from "../../hooks/customDocumentTitleHook";
import Accordion from "../UI/Accordion";
import { useLocation, useNavigate } from "react-router-dom";
import { Gender, calculateAgeByDos, exchangeDate, getPractice, notify, usDate, usPhoneFormat } from "../../helpers";
import { createChart, getAllMedication, getAssessmentData, loadRFVCategory, loadReasonForVisit, loadVisitType, saveAssessmentMedication, updateReasonForVisit } from "../../services/aiCharting";
import BackBtn from "../Button/BackBtn";


export default function NewAICharting({selectedPracticeChange, fetchChangePracticeData}) {
  const [dosDate, setDosDate] = useState(new Date());
  const [isChange, setIsChange] = useState(false);
  const [DBvtOption, setVtOptionVal] = useState(null);
  const [loadDefaultData, setLoadDefaultData] = useState(false);
  const [fetchDefaultData, setFetchDefaultData] = useState(false);
  const [aiChartData, setAiChartData] = useState([]);
  const [patientId, setPatientId] = useState(null);
  const location = useLocation();
  const { state } = location;
  const [vtOptions, setVtOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const resultsRef = useRef(null);
  const [defaultFetched, setdefaultFetched] = useState(false);
  const [rfvOptions, setRfvOptions] = useState([]);
  const navigate = useNavigate();
  const [openAccordions, setOpenAccordions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rfvDropdownShow, setRfvDropdownShow] = useState(false);
  const queryParams = new URLSearchParams(location.search);
  const [reasonForVisitData, setReasonForVisitData] = useState();
  const [reasonForVisitOptions, setReasonForVisitOptions] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [rfvIds, setRfvIds] = useState([]);


  const [reasonForVisitForm, setReasonForVisitForm] = useState({
    vt_ids: (reasonForVisitData && reasonForVisitData.vt_ids) || {},
    rfv_ids: (reasonForVisitData && reasonForVisitData.rfv_ids) || "",
    rfvData: (reasonForVisitData && reasonForVisitData.rfvData) || {},
    referral: (reasonForVisitData && reasonForVisitData.referral) || "",
    medication: (reasonForVisitData && reasonForVisitData.medication) || "",
  });

  let accountId = getPractice("account_id");
  let locationId = getPractice("location_id");
  if(selectedPracticeChange != null && fetchChangePracticeData){
    locationId = selectedPracticeChange?.location_id;
    accountId = selectedPracticeChange?.account_id;
    navigate('/ai-charting');
  }
  const paramPatientId = queryParams?.get('patient_id');
  const paramChartId = queryParams?.get("chart_no");

  useEffect(() => {
    loadAllVisitType();
    loadAllReasonForVisit();

    if (state) {
      setPatientId(state.patientid);
      getChartingData(accountId, locationId, state.patientid);
    } else if(paramPatientId != null) {
      setPatientId(paramPatientId);
      getChartingData(accountId, locationId, paramPatientId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, locationId, state, paramPatientId]);
  
  useEffect(() => {
    if (vtOptions?.length > 0 && reasonForVisitData) {
      setFetchDefaultData(true);
      const vtId = reasonForVisitData?.vt_ids?.vt_id;
      if (vtId) {
        const vtValues = vtOptions.find((option) => option.value === vtId);
        setVtOptionVal(vtValues);
      }

      Object.values(reasonForVisitData?.rfvData || {}).forEach((item) => {
        addRfvRepeater(item?.rfv_id, item);
      });

      Object.values(reasonForVisitData?.rfvData || {}).forEach((item) => {
        if (item.ros_cats) {
          Object.entries(item.ros_cats).forEach(([key, value]) => {
            handleRfvRepeaterChange(item.rfv_id, `ros_cats_${key}`, value, false)
          });
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vtOptions, reasonForVisitData]);

  const getChartingData = async (account_id, location_id, patient_id, date = new Date()) => {
    const chartData = {
      account_id: account_id,
      location_id: location_id,
      patient_id: parseInt(patient_id, 10),
      ...(paramChartId !== null 
        ? { chart_id: parseInt(paramChartId, 10) } 
        : { dos: usDate(date) })
    };
    try {
      const response = await createChart(chartData);

      if (response?.data) {
        if (response?.data?.status === "success") {
          const chartResult = response?.data?.chart_data;
          if(chartResult?.account_id !== accountId && chartResult?.location_id !== locationId){
            navigate('/ai-charting');
            return;
          }
          if(chartResult?.status === "Done"){
            navigate(`/ai-charting/view?patient_id=${patient_id}&chart_no=${chartResult?.id}`);
            return;
          }
          setAiChartData(chartResult);
          setDosDate(chartResult?.dos);

          if (!fetchDefaultData) {
            setReasonForVisitData(chartResult?.reason_for_visit_data);
            setReasonForVisitForm({
              vt_ids: chartResult?.reason_for_visit_data?.vt_ids || {},
              rfv_ids: chartResult?.reason_for_visit_data?.rfv_ids || "",
              rfvData: chartResult?.reason_for_visit_data?.rfvData || {},
              referral: chartResult?.reason_for_visit_data?.referral || "no",
              medication: chartResult?.reason_for_visit_data?.medication || "no",
            });
          }

          setLoadDefaultData(true);
        } else {
          notify("error", response?.data?.message);
          window.history.back();
          return;
        }
      }
    } catch (error) {
      // console.log("🚀 ~ getChartingData ~ error:", error);
      window.history.back();
      return;
    } finally {
      setDataLoading(false);
    }
  };

  // Load All Visit Type
  const loadAllVisitType = async () => {
    try {
      const response = await loadVisitType();
      if (response?.data) {
        const data = response?.data?.map((items) => ({
          value: items.id,
          label: items.vt_name,
          vt_hpi: items.vt_hpi,
        }));
        setVtOptions(data);
      }
    } catch (error) {
      // console.log("🚀 ~ loadAllVisitType ~ error:", error);
    }
  };

  // Load All Reason for Visit
  const loadAllReasonForVisit = async () => {
    try {
      const response = await loadReasonForVisit();
      if (response?.data) {
        setReasonForVisitOptions(response.data)
        const data = response?.data?.map((items) => ({
          value: items.id,
          label: items.rfv_name,
        }));
        setRfvOptions(data);
      }
    } catch (error) {
      // console.log("🚀 ~ loadAllReasonForVisit ~ error:", error);
    }
  };

  // Change Visit Type
  const vtHandleChange = (selectedOption) => {
    setIsChange(true);
    setVtOptionVal(selectedOption);
    reasonForVisitInput("vt_ids", {
      vt_id: selectedOption.value,
      vt_name: selectedOption.label,
      vt_hpi: selectedOption.vt_hpi,
    });
  };

  // Reason for Visit Search
  const handleSearch = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchTerm(value);
    const words = value.split(" ").filter(Boolean);
    const results = reasonForVisitOptions.filter((item) =>
      words.every((word) => item.rfv_name.toLowerCase().includes(word))
    );
    setSearchResults(results);
    setShowResults(value !== "" && results.length > 0);
    setRfvDropdownShow(true);
  };

  // Generate Reason for Visit Category
  const makeCategory = (cats, rfv_id, defaultValues) => {
    const rosCatOptions = cats.reduce((acc, cat) => {
      const rfvId = rfv_id;
      const catsId = cat.cats_id;
      const catsLabel = cat.cats_name;
      const isMultiple = cat.cats_type === 'multiple';

      if (!acc[catsLabel]) {
        acc[catsLabel] = {
          id: catsId,
          rfvid: rfvId,
          label: catsLabel,
          options: [],
          isMultiple: isMultiple,
          defaultValue: defaultValues && defaultValues &&  defaultValues.ros_cats && defaultValues.ros_cats[catsLabel]
        };
      }

      const options = cat.cats_options.map(option => ({
        value: option.option_value,
        label: option.option_name,
      }));

      acc[catsLabel].options.push(...options);

      return acc;
    }, {});

    const dynamicSelectComponents = Object.values(rosCatOptions).map((cats, index) => (
      <div key={index}>
        <label  className="docpro_fw_500 docpro_primary_color">{cats.label}</label>
        <Select
          onChange={(selectedOption) => handleRfvRepeaterChange(cats.rfvid, 'ros_cats_'+cats.label, selectedOption)}
          key={index}
          name={`cats_${index}_ids`}
          options={cats.options}
          placeholder={`${cats.label}`}
          isSearchable={true}
          isMulti={cats.isMultiple}
          defaultValue={cats.defaultValue}
          classNamePrefix="react-select"
          menuPortalTarget={document.body}
          menuPlacement="auto"
        />
      </div>     
    ));

    return dynamicSelectComponents;
  };

  // Change Reason For Visit
  const rfvHandleChange = (selectedOption) => {
    setIsChange(true);
    reasonForVisitInput("rfv_ids", selectedOption);
    addRfvRepeater(selectedOption);
    setSearchResults([]);
    setRfvDropdownShow(false);
  };

  const reasonForVisitInput = (field, value) => {
    setReasonForVisitForm({
      ...reasonForVisitForm,
      [field]: value,
    });
  };

  // Accordion Action
  const handleAccordion = (index) => {
    if (openAccordions.includes(index)) {
      setOpenAccordions(openAccordions.filter((item) => item !== index));
    } else {
      setOpenAccordions([...openAccordions, index]);
    }
  };

  // Set Input Changes Data
  const handleCreateChartInputChange = (key,value) => {
    setIsChange(true);
    setReasonForVisitForm({
      ...reasonForVisitForm,
      [key]: value,
    });
    if (key === 'dos') {
      setDosDate(value);
    }
  };

  const handleRfvRepeaterChange = (rfv_id, field, value, change=true) => {
    if(change) {
      setIsChange(true);
    }
    setReasonForVisitForm((prevRfvData) => ({
      ...prevRfvData,
      rfvData: {
        ...prevRfvData.rfvData,
        [rfv_id]: {
          ...prevRfvData.rfvData[rfv_id],
          [field]: value,
        },
      },
    }));
  };

  const addRfvRepeater = async (rfv_ids, default_category_selected_values = null) => {
    const existingRFVIds = rfvIds || [];
    const isAlreadyAdded = existingRFVIds.some((id) => id === rfv_ids);

    if (!isAlreadyAdded) {
      const updatedRFVIds = [...existingRFVIds, rfv_ids];
      setRfvIds(updatedRFVIds);
    }
    const result = await loadRFVCategory(rfv_ids);

    if (result) {
      const getCategory = makeCategory(result.data, rfv_ids, default_category_selected_values);

      // Ensure that default values are registered in state
      const defaultValues = {};
      if (default_category_selected_values) {
        Object.entries(default_category_selected_values.ros_cats || {}).forEach(([key, value]) => {
          defaultValues[`ros_cats_${key}`] = value;
        });
      }
  
      setReasonForVisitForm((prevRfvData) => {
        let duration = "";
        let duration_type = "";
        let extra_notes = "";
        let timestamp = Date.now();

        if (default_category_selected_values) {
          const rfv = default_category_selected_values;
          duration = rfv.duration;
          duration_type = rfv.duration_type;
          extra_notes = rfv.extra_notes;
          timestamp = rfv.timestamp;
        }

        const rfv_idsSelected = rfvOptions.find(
          (option) => option.value === rfv_ids
        );

        if (rfv_idsSelected) {
          const setRfv_id = rfv_idsSelected.value;
          return {
            ...prevRfvData,
            rfvData: {
              ...prevRfvData.rfvData,
              [setRfv_id]: {
                getDynamicCategoryOptions: getCategory,
                rfv_id: setRfv_id,
                rfv_name: rfv_idsSelected.label,
                duration,
                duration_type,
                extra_notes,
                timestamp: timestamp,
                ...defaultValues, // Add defaults here
              },
            },
          };
        } else if (prevRfvData.rfvData) {
          return {
            ...prevRfvData,
            rfvData: prevRfvData.rfvData,
          };
        } else {
          return {
            ...prevRfvData,
            rfvData: {},
          };
        }
      });
    }
  };

  const removeRfvRepeater = (rfv_id) => {          
    setIsChange(true);
    setReasonForVisitForm((prevRfvData) => {
      const newrfvData = { ...prevRfvData.rfvData };
      delete newrfvData[rfv_id];
      return {
        ...prevRfvData,
        rfvData: newrfvData,
      };
    });

    const existingRFVIds = rfvIds || [];
    const updatedRFVIds = existingRFVIds.filter(id => id.toString() !== rfv_id.toString());
    setRfvIds(updatedRFVIds);
  };

  const filterReasonForVisitForm = (rfvData) => {
    const newRfvData = {};
    for (const key in rfvData) {
      if (Object.prototype.hasOwnProperty.call(rfvData, key)) {
        const item = rfvData[key];
        const newRfvItem = { ...item };
        delete newRfvItem.getDynamicCategoryOptions;
        const rosCats = {};
        for (const subKey in newRfvItem) {
          if (subKey.startsWith('ros_cats_')) {
            const category = subKey.substring('ros_cats_'.length);
            rosCats[category] = newRfvItem[subKey];
            delete newRfvItem[subKey];
          }
        }
        if (Object.keys(rosCats).length > 0) {
          newRfvItem.ros_cats = rosCats;
        }

        newRfvData[key] = newRfvItem;
      }
    }
    return newRfvData;
  };

  useEffect(() => {
    if (!defaultFetched) {
      setdefaultFetched(true);
      if (!reasonForVisitData) {
        setLoadDefaultData(true);
      }
    }

    const handleClickOutside = (event) => {
      if (resultsRef.current && !resultsRef.current.contains(event.target)) {
        setSearchResults([]);
        setShowResults(true);
        setRfvDropdownShow(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [aiChartData, defaultFetched, reasonForVisitData]);

  const handleChartingSummmery = (patient_id) => {
    navigate(`/ai-charting/view?patient_id=${patient_id}&chart_no=${aiChartData?.id}`);
  };

  const saveToDb = async (hideToastify=false) => {
    // Filter data to include all default and changed values
    const updatedReasonForVisitForm = filterReasonForVisitForm(reasonForVisitForm.rfvData);

    // Include unchanged values
    const finalreasonForVisitForm = {
      ...reasonForVisitForm,
      rfvData: updatedReasonForVisitForm,
    };

    setReasonForVisitData(finalreasonForVisitForm);

    const patientInfoRawHpi = `${aiChartData ? calculateAgeByDos(aiChartData.dob, aiChartData.dos) + 'Y' : 'null'} old 
      ${aiChartData ? Gender(aiChartData.sex) : 'null'} patient came for 
      ${finalreasonForVisitForm.vt_ids.vt_hpi}.\nPatient has complaints of 
      ${finalreasonForVisitForm &&
        Object.values(finalreasonForVisitForm.rfvData)
          .map((rfvItem) => `${rfvItem.rfv_name} for ${rfvItem.duration} ${rfvItem.duration_type || 'Days'}`)
          .join(', ')
      }. ${finalreasonForVisitForm &&
        Object.values(finalreasonForVisitForm.rfvData)
          .map((rfvItem, index) =>
            rfvItem.ros_cats
              ? `${index === 0 ? 'Patient also mentioned that' : ' and'} ${
                  rfvItem.rfv_name
                } is ${Object.entries(rfvItem.ros_cats)
                  .map(([categoryKey, category]) =>
                    Array.isArray(category)
                      ? `${categoryKey} ${category.map((item) => item.value).join(', ')}`
                      : `${categoryKey} ${category.value}`
                  )
                  .join(', ')}.`
              : ''
          )
          .join('')
      }`;

    const updatedParams = {
      account_id: accountId,
      location_id: locationId,
      chart_id: aiChartData?.id,
      vt_id: finalreasonForVisitForm?.vt_ids?.vt_id,
      reason_for_visit_data: finalreasonForVisitForm,
      raw_hpi: patientInfoRawHpi,
    };

    const updateRFV = await updateReasonForVisit(updatedParams);
    if (updateRFV?.data) {
      if (updateRFV?.data?.status === 'success') {
        setIsChange(false);
        if (!hideToastify) {
          notify('success', updateRFV?.data?.message);
        }
      } else { 
        notify('error', updateRFV?.data?.message);
      }
    }
  };

  const AIChartSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const rfv_ids = Object.keys(reasonForVisitForm.rfvData).map(key => parseInt(key));
      if (rfv_ids.length === 0) {
        notify('error', 'Please select reason for visit!');
      } else {
        const medication_result = await getAllMedication(rfv_ids);
        if (medication_result?.data) { 
          const icdCodes = Object.values(medication_result?.data).reduce((x, item) => {
            const icdData = item.icd_data?.map(icd => icd.icd_code) || [];
            return x.concat(icdData);
          }, []);

          const assessment_result = await getAssessmentData(icdCodes);
          const assessment_data = assessment_result?.data?.filter(item => item !== null);

          if (assessment_data) {
            saveToDb(true);

            const medicationParams = { 
              chart_id: aiChartData?.id,
              "medication_data": medication_result?.data,
              "assessment_data": assessment_data
            }
            const save_assessment_medication = await saveAssessmentMedication(medicationParams);
            if (save_assessment_medication?.data) {
              if (save_assessment_medication?.data?.status === 'success') {
                navigate(`/ai-charting/view?patient_id=${patientId}&chart_no=${aiChartData?.id}`);
              } else { 
                notify('error', save_assessment_medication?.data?.message);
              }
            }
          }
        }
      }
    } catch (error) {
      notify('error', 'Failed to create the chart!');
    } finally {
      setIsSubmitting(false);
    }
  };

  useDocumentTitle("Create AI Chart");

  return (
    <>
      <BackBtn href="/ai-charting" btnText="Back to AI charting" />
      <Spacing lg="0" sm="24" />
      <div className="docpro_wrap">
        <div className="docpro_tab">
          <button type="button" className={`docpro_tab_item active`}>
            Reason
          </button>
          <button
            type="button"
            className={`docpro_tab_item`}
            onClick={() => handleChartingSummmery(patientId)}
          >
            Summary
          </button>
        </div>
      </div>
      <Spacing lg="8" sm="8" />

      <div className="docpro_data_table_wrap">
        <div className="docpro_card relative"style={{ minHeight: '70vh' }}>
          {!dataLoading ? (
            <form onSubmit={AIChartSubmit} className="flex custom-scroll" style={{ minHeight: '70vh', flex: '1 1', flexDirection: 'column' }}>
              <div>
                <div className="docpro_page_heading_wrap">
                  <h2 className="docpro_page_heading docpro_fs_24">
                    Create AI Chart
                  </h2>
                </div>
                <Spacing lg="8" sm="8" />

                <div className="docpro_grid docpro_grid_gap_10 docpro_grid_col_3 input">
                  <div className="docpro_grid_item">
                    <p className="mb-0"><strong style={{ minWidth: '90px', display: 'inline-block' }}>Last Name</strong><strong style={{ padding: '0 6px' }}>:</strong>{aiChartData ? aiChartData?.last_name : ""}</p>
                  </div>
                  <div className="docpro_grid_item">
                    <p className="mb-0"><strong style={{ minWidth: '90px', display: 'inline-block' }}>First Name</strong><strong style={{ padding: '0 6px' }}>:</strong>{aiChartData ? aiChartData?.first_name : ""}</p>
                  </div>
                  <div className="docpro_grid_item">
                    <p className="mb-0"><strong style={{ minWidth: '90px', display: 'inline-block' }}>DOB</strong><strong style={{ padding: '0 6px' }}>:</strong>{aiChartData ? exchangeDate(aiChartData?.dob) : ""}</p>
                  </div>
                  <div className="docpro_grid_item">
                    <p className="mb-0"><strong style={{ minWidth: '90px', display: 'inline-block' }}>Phone</strong><strong style={{ padding: '0 6px' }}>:</strong>{aiChartData ? usPhoneFormat(aiChartData?.mobile_phone) : ""}</p>
                  </div>
                  <div className="docpro_grid_item">
                    <p className="mb-0"><strong style={{ minWidth: '90px', display: 'inline-block' }}>Insurance</strong><strong style={{ padding: '0 6px' }}>:</strong>{aiChartData ? aiChartData?.insurance?.name : ""}</p>
                  </div>
                  <div className="docpro_grid_item">
                    <p className="mb-0"><strong style={{ minWidth: '90px', display: 'inline-block' }}>Insurance ID</strong><strong style={{ padding: '0 6px' }}>:</strong>{aiChartData ? aiChartData?.insurance_code : ""}</p>
                  </div>
                  <div className="docpro_grid_item">
                    <div className="mb-0 flex"><strong style={{ minWidth: '90px', display: 'inline-block' }}>DOS</strong><strong style={{ padding: '0 6px' }}>:</strong>
                      <div className="docpro_signout_input invoice_input">
                        <DatePicker
                          name="dos"
                          required
                          placeholderText="mm/dd/yyyy"
                          selected={dosDate}
                          onChange={(date) =>
                            handleCreateChartInputChange("dos", date)
                          }
                        />
                      </div>
                    </div>
                  </div>
                  <div className="docpro_grid_item">
                    <div className="mb-0 flex"><strong style={{ minWidth: '90px', display: 'inline-block' }}>Referral</strong><strong style={{ padding: '0 6px' }}>:</strong>
                      <div className="docpro_radio_group">
                        <div className="docpro_radio">
                          <input
                            type="radio"
                            name="referral"
                            value="yes"
                            required
                            defaultChecked={reasonForVisitForm?.referral === "yes"}
                            onChange={(e) => handleCreateChartInputChange('referral', e.target.value)}
                          />
                          <label style={{ paddingLeft: "30px" }}>Yes</label>
                        </div>
                        <div className="docpro_radio">
                          <input
                            type="radio"
                            name="referral"
                            value="no"
                            required
                            defaultChecked={reasonForVisitForm?.referral === "no"}
                            onChange={(e) => handleCreateChartInputChange('referral', e.target.value)}
                          />
                          <label style={{ paddingLeft: "30px" }}>No</label>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="docpro_grid_item">
                    <div className="mb-0 flex"><strong style={{ minWidth: '90px', display: 'inline-block' }}>Medication</strong><strong style={{ padding: '0 6px' }}>:</strong>
                      <div className="docpro_radio_group">
                        <div className="docpro_radio">
                          <input
                            type="radio"
                            name="medication"
                            value="yes"
                            required
                            defaultChecked={reasonForVisitForm?.medication === "yes"}
                            onChange={(e) => handleCreateChartInputChange('medication', e.target.value)}
                          />
                          <label style={{ paddingLeft: "30px" }}>Yes</label>
                        </div>
                        <div className="docpro_radio">
                          <input
                            type="radio"
                            name="medication"
                            value="no"
                            required
                            defaultChecked={reasonForVisitForm?.medication === "no"}
                            onChange={(e) => handleCreateChartInputChange('medication', e.target.value)}
                          />
                          <label style={{ paddingLeft: "30px" }}>No</label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <Spacing lg="36" sm="36" />

                <label className="docpro_fw_500 docpro_primary_color">
                  Visit type
                </label>
                <div className="size_lg">
                  <Select
                    required={loadDefaultData}
                    name="vt_id"
                    options={vtOptions}
                    onChange={vtHandleChange}
                    placeholder="Search.."
                    isSearchable={true}
                    value={DBvtOption}
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </div>

                <Spacing lg="16" sm="16" />
                <label className="docpro_fw_500 docpro_primary_color">
                  Reason for visit
                </label>
                <div className="docpro_search_form size_lg" ref={resultsRef}>
                  <button className="docpro_search_btn">
                    <Search width={20} height={20} />
                  </button>
                  <input
                    type="search"
                    placeholder="Search"
                    className="docpro_search_input"
                    value={searchTerm}
                    onChange={handleSearch}
                    onClick={handleSearch}
                    style={{ borderRadius: '4px' }}
                  />

                  <div className={`docpro_dropdown ${rfvDropdownShow ? 'open' : 'close'} custom-scroll`} style={{ maxHeight: '250px', width: '100%', overflowY: 'scroll' }}>
                    {!showResults && searchResults.length === 0 ? (
                      <p className="docpro_dropdown_item docpro_mb_0">
                        No results found
                      </p>
                    ) : (
                      searchResults.map((rfv) => {
                        return (
                          <div
                            key={rfv.id}
                            className="docpro_dropdown_item"
                            onClick={() => rfvHandleChange(rfv.id)}
                          >
                            {rfv.rfv_name}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {Object.entries(reasonForVisitForm.rfvData)    
                  .sort(([, a], [, b]) => a.timestamp - b.timestamp)
                  .map(([rfv_id, staticData], index) => (
                    <Accordion  key={index} title={ staticData.rfv_name} remove_accordion={removeRfvRepeater} accordion_id={rfv_id} accordion_no={index} handle_accordion={handleAccordion} open_accordions={openAccordions}>
                      <div className="docpro_grid docpro_grid_gap_20 docpro_grid_col_3">
                        <div className="docpro_grid_item">
                          <label className="docpro_fw_500 docpro_primary_color">
                            Duration
                          </label>
                          <div className="docpro_form_field_group">
                            <input
                              onChange={(e) => handleRfvRepeaterChange(rfv_id, 'duration', e.target.value)}
                              type="number"
                              required
                              defaultValue={staticData.duration}
                              min={0}
                              name="duration"
                              autoComplete="duration"
                              className="docpro_form_field size_lg"
                              style={{ borderRadius: '4px 0 0 4px' }}
                            />
                            <select
                              className="docpro_form_field size_lg"
                              // onChange={(e) => handleRfvRepeaterChange(rfv_id, 'duration_type', e.target.value)}
                              // defaultValue={staticData.duration_type}
                              onChange={(e) => handleRfvRepeaterChange(rfv_id, 'duration_type', e.target.value)}
                              value={staticData.duration_type || ""}
                              name="duration_type"
                              style={{ borderRadius: '0 4px 4px 0' }}
                            >
                              <option value="Days">Days</option>
                              <option value="Weeks">Weeks</option>
                              <option value="Months">Months</option>
                              <option value="Years">Years</option>
                            </select>
                          </div>
                        </div>
                        {staticData.getDynamicCategoryOptions && (staticData.getDynamicCategoryOptions).map((category, index) => (
                          <div className="docpro_grid_item" key={index}>{category}</div>
                        ))}
                      </div>
                      <Spacing lg="20" sm="20" />
                      <label className="docpro_fw_500 docpro_primary_color">
                        Extra Notes
                      </label>
                      <textarea
                        cols="30"
                        rows="2"
                        name="extra_notes"
                        defaultValue={staticData.extra_notes}                                      
                        onChange={(e) => handleRfvRepeaterChange(rfv_id, 'extra_notes', e.target.value)}
                        className="docpro_form_field"
                        style={{ borderRadius: '4px' }}
                      ></textarea>
                    </Accordion>
                ))}
              </div>

              <div style={{ marginTop: 'auto' }}>
                <Spacing lg="20" sm="20" />
                <div className="docpro_modal_btns">
                  <button onClick={() => saveToDb()} className={`save-rfv ${isChange ? 'block' : 'hidden'} docpro_bordered_btn docpro_fw_600`}  type="button" >
                    SAVE
                  </button>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="docpro_bordered_btn docpro_fw_600 docpro_white_color docpro_filed_btn size_lg"
                  >
                    Next
                    <ChevronRight width={20} height={20} />
                  </button>
                </div>
              </div>
            </form>
          ) : (
            <div className="absolute top-0 left-0 w-full" style={{ height: '100%', zIndex: 3 }}>
              <div className="flex items-center loading" style={{ width: '100%', height: '100%', justifyContent: 'center', background: 'rgb(255 255 255 / 63%)', borderRadius: '10px' }}>
                <span className="docpro_loading" style={{ position: 'inherit' }}>
                  <Loader width={46} height={46} style={{ color: 'var(--accent)' }} />
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
